import React, { useState, useEffect } from 'react';
import {
    X,
    User,
    Shield,
    CheckCircle2,
    Clock,
    AlertTriangle,
    Mail,
    Calendar,
    ChevronRight,
    Search,
    Filter,
    FileText
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const EmployeeComplianceDrawer = ({ isOpen, onClose, employeeId }) => {
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState(null);
    const [activeTab, setActiveTab] = useState('tasks'); // 'tasks', 'violations'

    useEffect(() => {
        if (isOpen && employeeId) {
            fetchProfile();
        }
    }, [isOpen, employeeId]);

    const fetchProfile = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/compliance/employees/${employeeId}`);
            setProfile(response.data.profile);
        } catch (error) {
            console.error('Error fetching employee compliance profile:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleExportProfile = async (format) => {
        try {
            const toastId = toast.loading(`Exporting profile as ${format.toUpperCase()}...`);
            const response = await api.get('/compliance/report/export', {
                params: {
                    type: 'profile',
                    format,
                    employeeId
                },
                responseType: format === 'json' ? 'json' : 'blob'
            });

            if (format === 'json') {
                const dataStr = JSON.stringify(response.data.report, null, 2);
                const dataBlob = new Blob([dataStr], { type: 'application/json' });
                const url = URL.createObjectURL(dataBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `compliance-profile-${profile.employee.first_name}-${profile.employee.last_name}.json`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            } else {
                const blob = new Blob([response.data], {
                    type: format === 'pdf' ? 'application/pdf' : 'text/csv'
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `compliance-profile-${profile.employee.first_name}-${profile.employee.last_name}.${format}`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            }
            toast.success('Profile exported successfully', { id: toastId });
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to export profile');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={onClose} />
            <div className="absolute inset-y-0 right-0 w-full max-w-2xl bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white z-10">
                    <h2 className="text-xl font-semibold text-gray-800">Compliance Profile</h2>
                    <div className="flex items-center gap-2">
                        {profile && (
                            <div className="flex gap-2 mr-4">
                                <button
                                    onClick={() => handleExportProfile('csv')}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500 hover:text-gray-700"
                                    title="Export CSV"
                                >
                                    <FileText className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => handleExportProfile('pdf')}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500 hover:text-gray-700"
                                    title="Export PDF"
                                >
                                    <FileText className="w-5 h-5" />
                                </button>
                            </div>
                        )}
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <X className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="flex-1 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                ) : profile ? (
                    <div className="flex-1 overflow-y-auto">
                        {/* Profile Summary */}
                        <div className="p-6 bg-gray-50/50 border-b border-gray-100">
                            <div className="flex items-start gap-4">
                                <div className="w-16 h-16 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xl font-bold">
                                    {profile.employee.first_name[0]}{profile.employee.last_name[0]}
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-lg font-bold text-gray-900">
                                        {profile.employee.first_name} {profile.employee.last_name}
                                    </h3>
                                    <p className="text-gray-500 text-sm mb-2">{profile.employee.position} • {profile.employee.department || 'No Dept'}</p>

                                    <div className="flex gap-4 mt-3">
                                        <div className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-600">
                                            <Mail className="w-4 h-4 text-gray-400" />
                                            {profile.employee.email}
                                        </div>
                                        <div className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-600">
                                            <Calendar className="w-4 h-4 text-gray-400" />
                                            Joined {new Date(profile.employee.joined_at).toLocaleDateString()}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg shadow-sm">
                                        <Shield className={`w-5 h-5 ${profile.stats.compliance_score >= 90 ? 'text-green-500' :
                                            profile.stats.compliance_score >= 70 ? 'text-yellow-500' : 'text-red-500'
                                            }`} />
                                        <div className="text-left">
                                            <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Score</p>
                                            <p className="text-lg font-bold text-gray-900 leading-none">{profile.stats.compliance_score}%</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Stats Grid */}
                            <div className="grid grid-cols-3 gap-4 mt-6">
                                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                                    <p className="text-sm text-gray-500 mb-1">Completed Tasks</p>
                                    <div className="flex items-end justify-between">
                                        <span className="text-2xl font-bold text-gray-900">{profile.stats.completed_tasks}</span>
                                        <span className="text-xs text-gray-400">/ {profile.stats.total_tasks} total</span>
                                    </div>
                                </div>
                                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                                    <p className="text-sm text-gray-500 mb-1">Overdue Tasks</p>
                                    <div className="flex items-end justify-between">
                                        <span className={`text-2xl font-bold ${profile.stats.overdue_tasks > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                                            {profile.stats.overdue_tasks}
                                        </span>
                                        <Clock className={`w-4 h-4 mb-1 ${profile.stats.overdue_tasks > 0 ? 'text-red-500' : 'text-gray-300'}`} />
                                    </div>
                                </div>
                                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                                    <p className="text-sm text-gray-500 mb-1">Active Violations</p>
                                    <div className="flex items-end justify-between">
                                        <span className={`text-2xl font-bold ${profile.stats.active_violations > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                                            {profile.stats.active_violations}
                                        </span>
                                        <AlertTriangle className={`w-4 h-4 mb-1 ${profile.stats.active_violations > 0 ? 'text-red-500' : 'text-gray-300'}`} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Tabs */}
                        <div className="px-6 border-b border-gray-100 sticky top-0 bg-white z-10 pt-2">
                            <div className="flex items-center gap-6">
                                <button
                                    onClick={() => setActiveTab('tasks')}
                                    className={`py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'tasks' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    Task History
                                </button>
                                <button
                                    onClick={() => setActiveTab('violations')}
                                    className={`py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'violations' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    Violation Log
                                </button>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-6">
                            {activeTab === 'tasks' ? (
                                <div className="space-y-3">
                                    {profile.tasks.length === 0 ? (
                                        <div className="text-center py-8 text-gray-500">No compliance tasks assigned.</div>
                                    ) : (
                                        profile.tasks.map(task => (
                                            <div key={task.assignment_id} className="p-4 bg-white border border-gray-100 rounded-lg hover:shadow-sm transition-shadow">
                                                <div className="flex items-start justify-between">
                                                    <div>
                                                        <h4 className="font-medium text-gray-900 mb-1">{task.title}</h4>
                                                        <div className="flex items-center gap-2 text-xs text-gray-500">
                                                            <span className="capitalize px-2 py-0.5 bg-gray-100 rounded">{task.task_type}</span>
                                                            <span>Due: {new Date(task.due_date).toLocaleDateString()}</span>
                                                        </div>
                                                    </div>
                                                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${task.status === 'completed' ? 'bg-green-100 text-green-700' :
                                                        task.status === 'overdue' ? 'bg-red-100 text-red-700' :
                                                            'bg-gray-100 text-gray-700'
                                                        }`}>
                                                        {task.status}
                                                    </span>
                                                </div>
                                                {task.completed_at && (
                                                    <div className="mt-3 pt-3 border-t border-gray-50 flex items-center justify-between text-xs">
                                                        <span className="text-gray-500">Completed on {new Date(task.completed_at).toLocaleDateString()}</span>
                                                        {task.score !== null && (
                                                            <span className="font-medium">Score: {task.score}%</span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {profile.violations.length === 0 ? (
                                        <div className="text-center py-8 text-gray-500">
                                            <CheckCircle2 className="w-12 h-12 text-green-100 mx-auto mb-2" />
                                            <p>No compliance violations found.</p>
                                        </div>
                                    ) : (
                                        profile.violations.map(violation => (
                                            <div key={violation.id} className="p-4 bg-white border border-l-4 border-l-red-500 border-gray-100 rounded-lg">
                                                <div className="flex justify-between items-start mb-2">
                                                    <h4 className="font-medium text-gray-900">{violation.violation_type}</h4>
                                                    <span className={`text-xs px-2 py-1 rounded-full capitalize ${violation.status === 'active' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                                                        }`}>
                                                        {violation.status}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-gray-600 mb-2">{violation.description}</p>
                                                {violation.task_title && (
                                                    <p className="text-xs text-gray-500">Related Task: {violation.task_title}</p>
                                                )}
                                                <div className="mt-2 text-xs text-gray-400">
                                                    Detected: {new Date(violation.created_at).toLocaleDateString()}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default EmployeeComplianceDrawer;
